"use client";

import { useAction, useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useState, useCallback, useEffect } from "react";
import { Browser } from "@capacitor/browser";
import { App as CapApp, URLOpenListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

// Always use HTTPS redirect (Google doesn't allow custom URL schemes)
// Production URL for native apps, current origin for web
const PRODUCTION_REDIRECT_URI = "https://flextrack.pages.dev/oauth/google/callback";

function getRedirectUri(): string {
  // In native app, always use production URL since it will redirect back to app
  if (Capacitor.isNativePlatform()) {
    return PRODUCTION_REDIRECT_URI;
  }
  // On web, use current origin
  return `${window.location.origin}/oauth/google/callback`;
}

export function useGmailAuth() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connection = useQuery(api.email.oauth.getConnection);
  const getAuthUrl = useAction(api.email.oauth.getAuthUrl);
  const exchangeCode = useAction(api.email.oauth.exchangeCode);
  const disconnectMutation = useMutation(api.email.oauth.disconnect);
  const revokeAndDisconnect = useAction(api.email.oauth.revokeAndDisconnect);

  // Handle OAuth callback on web
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthError = params.get("error");

    if (oauthError) {
      setError(`OAuth error: ${oauthError}`);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (code && !isConnecting) {
      setIsConnecting(true);
      exchangeCode({ code, redirectUri: getRedirectUri() })
        .then(() => {
          // Clean URL after successful exchange
          window.history.replaceState({}, "", window.location.pathname);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "Connection failed");
        })
        .finally(() => {
          setIsConnecting(false);
        });
    }
  }, [exchangeCode, isConnecting]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const redirectUri = getRedirectUri();
      const authUrl = await getAuthUrl({ redirectUri });

      if (Capacitor.isNativePlatform()) {
        // Native: Listen for deep link callback
        const listener = await CapApp.addListener(
          "appUrlOpen",
          async (event: URLOpenListenerEvent) => {
            try {
              const url = new URL(event.url);
              const code = url.searchParams.get("code");
              const oauthError = url.searchParams.get("error");

              if (oauthError) {
                setError(`OAuth error: ${oauthError}`);
                setIsConnecting(false);
                listener.remove();
                return;
              }

              if (code) {
                await exchangeCode({ code, redirectUri });
                listener.remove();
                setIsConnecting(false);
              }
            } catch (err) {
              setError(err instanceof Error ? err.message : "Connection failed");
              setIsConnecting(false);
              listener.remove();
            }
          }
        );

        // Open Google OAuth in system browser
        await Browser.open({ url: authUrl });
      } else {
        // Web: Redirect to OAuth URL
        window.location.href = authUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connection failed");
      setIsConnecting(false);
    }
  }, [getAuthUrl, exchangeCode]);

  const disconnect = useCallback(async () => {
    try {
      // Try to revoke at Google first, then delete locally
      await revokeAndDisconnect();
    } catch {
      // Fallback to just deleting locally
      await disconnectMutation();
    }
  }, [revokeAndDisconnect, disconnectMutation]);

  return {
    connection,
    isConnecting,
    error,
    connect,
    disconnect,
    isLoading: connection === undefined,
  };
}
